"use client";

import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";

export default function AboutPage() {
  return (
    <main className="container mx-auto px-4 py-12 md:py-24">
      {/* Hero Section */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-24">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            We are <span className="text-primary">BornAI</span>.
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed mb-6">
            More than just a software company, we are the bridge between traditional heavy industries and the era of artificial intelligence.
          </p>
          <div className="space-y-4 border-l-2 border-primary/30 pl-6">
            <div>
              <h3 className="text-lg font-semibold text-white">Borna (Persian: برنا)</h3>
              <p className="text-muted-foreground text-sm">Symbolizing youth, vitality, and growth within ancient structures.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Born (English)</h3>
              <p className="text-muted-foreground text-sm">Symbolizing genesis, creation, and a new beginning.</p>
            </div>
          </div>
        </motion.div>

        {/* بخش تصویر بهینه شده */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative h-[450px] w-full"
        >
          {/* افکت هاله نوری پشت تصویر */}
          <div className="absolute inset-0 bg-primary/10 blur-[120px] rounded-full"></div>
          
          <motion.div 
            animate={{ y: [0, -15, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative h-full w-full bg-secondary/30 rounded-3xl overflow-hidden border border-white/10 flex items-center justify-center shadow-2xl"
          >
            {/* تصویر اصلی */}
            <Image 
              src="/about-hero.png" 
              alt="BornAI Industrial Intelligence Visualization"
              fill
              className="object-cover opacity-80 group-hover:opacity-100 transition-opacity"
              priority
            />
            
            {/* Overlay برای خوانایی و جذابیت */}
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
            
            {/* متن محو پس‌زمینه برای عمق دادن */}
            <span className="absolute bottom-4 right-6 text-6xl font-black text-white/5 select-none tracking-tighter">
              BORN AI
            </span>
          </motion.div>
        </motion.div>
      </section>

      {/* Mission Section */}
      <motion.section 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="bg-secondary/30 rounded-3xl p-8 md:p-12 border border-white/5 text-center max-w-4xl mx-auto relative overflow-hidden"
      >
        {/* المان تزئینی پس‌زمینه */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/5 blur-[80px] rounded-full"></div>
        
        <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8 relative z-10">
          Imagine an old oil refinery, a car assembly line, or a traditional banking system being <span className="text-white font-medium">"reborn"</span> with Artificial Intelligence. That is the essence of BornAI. We exist to inject intelligence into the vital veins of industry, transforming data into decision-making power.
        </p>
        <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white px-8">
          <Link href="/careers">
            Work With Us <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </motion.section>
    </main>
  );
}